import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../Breadcrumb';

interface FieldAnalysis {
  type: 'numeric' | 'categorical' | 'empty';
  sample_count: number;
  min?: number;
  max?: number;
  mean?: number;
  median?: number;
  unique_values?: string[];
  unique_count?: number;
}

interface AnalysisResult {
  total_samples: number;
  analyzed_samples: number;
  field_analysis: { [key: string]: FieldAnalysis };
}

interface SyntheticDataItem {
  syn_id: string;
  [key: string]: any;
}

const SyntheticDataset: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({ dataAnalysis: true });
  const [predefinedFields, setPredefinedFields] = useState<{ [key: string]: boolean }>({});
  const [customValues, setCustomValues] = useState<{ [key: string]: string[] }>({});
  const [editingValue, setEditingValue] = useState<{ field: string; index: number } | null>(null);
  const [newValueInput, setNewValueInput] = useState<{ [key: string]: string }>({});
  const [syntheticData, setSyntheticData] = useState<any[]>([]);
  const [validatedItems, setValidatedItems] = useState<{ [key: string]: boolean }>({});
  const [generating, setGenerating] = useState(false);
  const [numDataPoints, setNumDataPoints] = useState(10);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    analyzeDataset();
  }, [projectId]);

  const analyzeDataset = async () => {
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/synthetic/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        console.error('Failed to analyze dataset');
      }
    } catch (error) {
      console.error('Error analyzing dataset:', error);
    }
    setLoading(false);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const togglePredefinedField = (field: string) => {
    setPredefinedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const addCustomValue = (field: string) => {
    const value = newValueInput[field]?.trim();
    if (value) {
      setCustomValues(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value]
      }));
      setNewValueInput(prev => ({ ...prev, [field]: '' }));
    }
  };

  const removeCustomValue = (field: string, index: number) => {
    setCustomValues(prev => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index) || []
    }));
  };

  const updateCustomValue = (field: string, index: number, newValue: string) => {
    setCustomValues(prev => ({
      ...prev,
      [field]: prev[field]?.map((val, i) => i === index ? newValue : val) || []
    }));
    setEditingValue(null);
  };

  const generateSyntheticData = async () => {
    if (!analysis) return;
    
    setGenerating(true);
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      // Merge custom values into field analysis
      const updatedFieldAnalysis = { ...analysis.field_analysis };
      Object.entries(customValues).forEach(([field, values]) => {
        if (updatedFieldAnalysis[field] && values.length > 0) {
          updatedFieldAnalysis[field] = {
            ...updatedFieldAnalysis[field],
            unique_values: [...(updatedFieldAnalysis[field].unique_values || []), ...values]
          };
        }
      });
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/synthetic/generate`, {
        method: 'POST',
        headers: {
          'Authorization': authToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          field_analysis: updatedFieldAnalysis,
          predefined_fields: predefinedFields,
          num_points: numDataPoints
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyntheticData(prev => [...prev, ...data.synthetic_data]);
      } else {
        console.error('Failed to generate synthetic data');
      }
    } catch (error) {
      console.error('Error generating synthetic data:', error);
    }
    setGenerating(false);
  };

  const toggleValidation = (synId: string) => {
    setValidatedItems(prev => ({
      ...prev,
      [synId]: !prev[synId]
    }));
  };

  const toggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newValidatedItems: { [key: string]: boolean } = {};
    syntheticData.forEach(item => {
      newValidatedItems[item.syn_id] = newSelectAll;
    });
    setValidatedItems(newValidatedItems);
  };

  const removeSyntheticItem = (synId: string) => {
    setSyntheticData(prev => prev.filter(item => item.syn_id !== synId));
    setValidatedItems(prev => {
      const { [synId]: removed, ...rest } = prev;
      return rest;
    });
  };

  const addToDataset = async () => {
    const validatedData = syntheticData.filter(item => validatedItems[item.syn_id]);
    if (validatedData.length === 0) return;
    
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      // Convert to CSV format for upload
      const headers = Object.keys(validatedData[0]).filter(k => k !== 'syn_id');
      const csvContent = [
        headers.join(','),
        ...validatedData.map(row => headers.map(h => row[h] || '').join(','))
      ].join('\n');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/upload`, {
        method: 'POST',
        headers: {
          'Authorization': authToken || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileContent: btoa(csvContent),
          fileName: 'synthetic_data.csv',
          replacementMode: false
        })
      });
      
      if (response.ok) {
        // Remove added items from synthetic data
        const addedIds = validatedData.map(item => item.syn_id);
        setSyntheticData(prev => prev.filter(item => !addedIds.includes(item.syn_id)));
        setValidatedItems({});
      }
    } catch (error) {
      console.error('Error adding to dataset:', error);
    }
  };

  if (loading) {
    return (
      <div className="module-content">
        <Breadcrumb items={[
          { label: 'Home', path: '/' },
          { label: 'Projects', path: '/' },
          { label: projectId || '', path: `/project/${projectId}` },
          { label: 'Test', path: `/project/${projectId}/test` },
          { label: 'Dataset', path: `/project/${projectId}/test/dataset` },
          { label: 'Generate Synthetic' }
        ]} />
        
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h3>Analyzing data...</h3>
          <div style={{ fontSize: '1.5rem', color: '#007bff' }}>
            <span>.</span>
            <span style={{ animationDelay: '0.2s' }}>.</span>
            <span style={{ animationDelay: '0.4s' }}>.</span>
          </div>
          <style>{`
            @keyframes blink {
              0%, 50% { opacity: 1; }
              51%, 100% { opacity: 0; }
            }
            div span {
              animation: blink 1.5s infinite;
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="module-content">
        <Breadcrumb items={[
          { label: 'Home', path: '/' },
          { label: 'Projects', path: '/' },
          { label: projectId || '', path: `/project/${projectId}` },
          { label: 'Test', path: `/project/${projectId}/test` },
          { label: 'Dataset', path: `/project/${projectId}/test/dataset` },
          { label: 'Generate Synthetic' }
        ]} />
        
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h3>Failed to analyze dataset</h3>
          <button onClick={() => navigate(`/project/${projectId}/test/dataset`)}>
            Back to Dataset
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId || '', path: `/project/${projectId}` },
        { label: 'Test', path: `/project/${projectId}/test` },
        { label: 'Dataset', path: `/project/${projectId}/test/dataset` },
        { label: 'Generate Synthetic' }
      ]} />
      
      <h2>Generate Synthetic Dataset</h2>
      
      {/* Data Analysis Section */}
      <div style={{ marginBottom: '2rem', border: '1px solid #ddd', borderRadius: '8px' }}>
        <div 
          onClick={() => toggleSection('dataAnalysis')}
          style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: expandedSections.dataAnalysis ? '8px 8px 0 0' : '8px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h3 style={{ margin: 0 }}>📊 Data Analysis</h3>
          <span style={{ fontSize: '1.2rem' }}>
            {expandedSections.dataAnalysis ? '▼' : '▶'}
          </span>
        </div>
        
        {expandedSections.dataAnalysis && (
          <div style={{ padding: '1rem' }}>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Analyzed {analysis.analyzed_samples} of {analysis.total_samples} samples
            </p>
            
            {Object.entries(analysis.field_analysis).map(([field, fieldData]) => (
              <div key={field} style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{field}</h4>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                  Type: <strong>{fieldData.type}</strong> | Samples: <strong>{fieldData.sample_count}</strong>
                </div>
                
                {fieldData.type === 'numeric' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginTop: '0.5rem' }}>
                    <div>Min: <strong>{fieldData.min?.toFixed(2)}</strong></div>
                    <div>Max: <strong>{fieldData.max?.toFixed(2)}</strong></div>
                    <div>Mean: <strong>{fieldData.mean?.toFixed(2)}</strong></div>
                    <div>Median: <strong>{fieldData.median?.toFixed(2)}</strong></div>
                  </div>
                )}
                
                {fieldData.type === 'categorical' && (
                  <div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={predefinedFields[field] || false}
                          onChange={() => togglePredefinedField(field)}
                        />
                        Predefined values field
                      </label>
                    </div>
                    
                    {predefinedFields[field] && (
                      <div style={{ 
                        padding: '0.5rem', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        borderRadius: '4px',
                        marginBottom: '0.5rem',
                        fontSize: '0.85rem',
                        color: '#856404'
                      }}>
                        ⚠️ Please verify all allowed values are included in the list below
                      </div>
                    )}
                    
                    <div>
                      <strong>Unique values ({fieldData.unique_count}):</strong>
                      <div style={{ 
                        marginTop: '0.25rem', 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '0.25rem',
                        alignItems: 'center'
                      }}>
                        {fieldData.unique_values?.map((value, idx) => (
                          <span 
                            key={idx}
                            style={{
                              padding: '0.2rem 0.5rem',
                              backgroundColor: '#e9ecef',
                              borderRadius: '12px',
                              fontSize: '0.8rem'
                            }}
                          >
                            {value}
                          </span>
                        ))}
                        {customValues[field]?.map((value, idx) => (
                          <div key={`custom-${idx}`} style={{ position: 'relative', display: 'inline-block' }}>
                            {editingValue?.field === field && editingValue?.index === idx ? (
                              <input
                                type="text"
                                defaultValue={value}
                                autoFocus
                                onBlur={(e) => updateCustomValue(field, idx, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateCustomValue(field, idx, e.currentTarget.value);
                                  }
                                }}
                                style={{
                                  padding: '0.2rem 0.5rem',
                                  border: '1px solid #007bff',
                                  borderRadius: '12px',
                                  fontSize: '0.8rem',
                                  minWidth: '60px'
                                }}
                              />
                            ) : (
                              <span
                                onClick={() => setEditingValue({ field, index: idx })}
                                style={{
                                  padding: '0.2rem 1.5rem 0.2rem 0.5rem',
                                  backgroundColor: '#d4edda',
                                  borderRadius: '12px',
                                  fontSize: '0.8rem',
                                  cursor: 'pointer',
                                  position: 'relative'
                                }}
                              >
                                {value}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeCustomValue(field, idx);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    right: '2px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  −
                                </button>
                              </span>
                            )}
                          </div>
                        ))}
                        {predefinedFields[field] && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <input
                              type="text"
                              placeholder="Add value"
                              value={newValueInput[field] || ''}
                              onChange={(e) => setNewValueInput(prev => ({ ...prev, [field]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  addCustomValue(field);
                                }
                              }}
                              style={{
                                padding: '0.2rem 0.5rem',
                                border: '1px solid #ccc',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                minWidth: '80px'
                              }}
                            />
                            <button
                              onClick={() => addCustomValue(field)}
                              style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              +
                            </button>
                          </div>
                        )}
                        {fieldData.unique_count && fieldData.unique_count > 20 && (
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>
                            ... and {fieldData.unique_count - 20} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Synthetic Data Generation Section */}
      <div style={{ marginBottom: '2rem', border: '1px solid #ddd', borderRadius: '8px' }}>
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0 }}>🤖 Generate Synthetic Data</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="number"
              min="1"
              max="50"
              value={numDataPoints}
              onChange={(e) => setNumDataPoints(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
              style={{
                width: '60px',
                padding: '0.25rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                textAlign: 'center'
              }}
            />
            <button
              onClick={generateSyntheticData}
              disabled={generating}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: generating ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: generating ? 'not-allowed' : 'pointer'
              }}
            >
              {generating ? 'Generating...' : `Generate ${numDataPoints} Data Points`}
            </button>
          </div>
        </div>
        
        {syntheticData.length > 0 && (
          <div style={{ padding: '1rem' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        title="Select All"
                      />
                    </th>
                    <th style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>Actions</th>
                    {Object.keys(syntheticData[0]).filter(k => k !== 'syn_id').map(key => (
                      <th key={key} style={{ padding: '0.5rem', border: '1px solid #ddd' }}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {syntheticData.map((row) => (
                    <tr key={row.syn_id} style={{ backgroundColor: validatedItems[row.syn_id] ? '#d4edda' : 'white' }}>
                      <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={validatedItems[row.syn_id] || false}
                          onChange={() => toggleValidation(row.syn_id)}
                        />
                      </td>
                      <td style={{ padding: '0.5rem', border: '1px solid #ddd', textAlign: 'center' }}>
                        <button
                          onClick={() => removeSyntheticItem(row.syn_id)}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ×
                        </button>
                      </td>
                      {Object.entries(row).filter(([k]) => k !== 'syn_id').map(([key, value]) => (
                        <td key={key} style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                          {String(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button
                onClick={addToDataset}
                disabled={Object.values(validatedItems).filter(Boolean).length === 0}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: Object.values(validatedItems).filter(Boolean).length > 0 ? '#28a745' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: Object.values(validatedItems).filter(Boolean).length > 0 ? 'pointer' : 'not-allowed'
                }}
              >
                Add {Object.values(validatedItems).filter(Boolean).length} to Dataset
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyntheticDataset;